import { IFilter } from "@saleor/components/Filter";
import { UseNavigatorResult } from "@saleor/hooks/useNavigator";
import { ActiveTab, Pagination, Search, Sort } from "@saleor/types";
import {
  GetFilterQueryParam,
  getFilterQueryParams
} from "@saleor/utils/filters";

type RequiredParams = ActiveTab & Search & Sort & Pagination;
type CreateUrl = (id: string, params: RequiredParams) => string;
type CreateFilterHandlers<TFilterKeys extends string> = [
  (id: string, filter: IFilter<TFilterKeys>, keepActiveTab?: boolean) => void,
  (id: string) => void,
  (id: string, query: string) => void
];

function createFilterHandlersWithId<
  TFilterKeys extends string,
  TFilters extends object
>(opts: {
  id: string;
  getFilterQueryParam: GetFilterQueryParam<TFilterKeys, TFilters>;
  navigate: UseNavigatorResult;
  createUrl: CreateUrl;
  params: RequiredParams;
  cleanupFn?: () => void;
}): CreateFilterHandlers<TFilterKeys> {
  const { getFilterQueryParam, navigate, createUrl, params, cleanupFn } = opts;

  const changeFilters = (
    id: string,
    filter: IFilter<TFilterKeys>,
    keepActiveTab?: boolean
  ) => {
    if (!!cleanupFn) {
      cleanupFn();
    }

    navigate(
      createUrl(id, {
        ...params,
        ...getFilterQueryParams(filter, getFilterQueryParam),
        activeTab: keepActiveTab ? params.activeTab : undefined
      })
    );
  };

  const resetFilters = (id: string) => {
    if (!!cleanupFn) {
      cleanupFn();
    }

    navigate(
      createUrl(id, {
        asc: params.asc,
        sort: params.sort
      })
    );
  };

  const handleSearchChange = (id: string, query: string) => {
    if (!!cleanupFn) {
      cleanupFn();
    }

    navigate(
      createUrl(id, {
        ...params,
        activeTab: undefined,
        query
      })
    );
  };

  return [changeFilters, resetFilters, handleSearchChange];
}

export default createFilterHandlersWithId;
